import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/solid';
import { useUnitStore } from '../store/unitStore';
import { useAuthStore } from '../store/authStore';
import clsx from 'clsx';

export default function UnitFilter() {
  const { units, selectedUnitIds, toggleUnitFilter, setSelectedUnitIds, isHQ } = useUnitStore();
  const { user } = useAuthStore();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Only show for Brigade HQ (users without unitId)
  const userIsHQ = isHQ(user?.unitId || null);
  if (!userIsHQ) {
    return null;
  }

  // Filter out the HQ unit - it's represented by the "All Units" option
  const selectableUnits = units.filter(u => u.role !== 'HQ');
  const allSelected = selectedUnitIds.length === selectableUnits.length && selectableUnits.length > 0;
  const noneSelected = selectedUnitIds.length === 0;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedUnitIds([]);
    } else {
      setSelectedUnitIds(selectableUnits.map(b => b.id));
    }
  };

  const getDisplayText = () => {
    if (noneSelected || allSelected) {
      return 'All Units';
    }
    if (selectedUnitIds.length === 1) {
      const unit = selectableUnits.find(b => b.id === selectedUnitIds[0]);
      return unit?.name || '1 Unit';
    }
    return `${selectedUnitIds.length} Units`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        className={clsx(
          'px-4 py-2 min-h-11 rounded-lg bg-command-panel border border-command-border',
          'text-sm text-gray-100 flex items-center gap-2 touch-manipulation',
          'hover:border-command-accent transition-colors'
        )}
      >
        <span className="font-semibold">Filter:</span>
        <span>{getDisplayText()}</span>
        <ChevronDownIcon className={clsx(
          'w-4 h-4 text-gray-400 transition-transform',
          showDropdown && 'rotate-180'
        )} />
      </button>

      {showDropdown && (
        <div className="absolute right-0 sm:right-auto sm:left-0 z-50 mt-1 w-64 max-w-[calc(100vw-1.5rem)] bg-command-bg border border-command-border rounded-lg shadow-lg max-h-80 overflow-auto">
          <div className="p-2 border-b border-command-border">
            <button
              type="button"
              onClick={handleSelectAll}
              className="w-full px-3 py-2 text-left text-sm text-gray-100 hover:bg-command-accent/20 rounded transition-colors"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="p-2">
            {selectableUnits.map((unit) => {
              const isSelected = selectedUnitIds.includes(unit.id);
              return (
                <button
                  key={unit.id}
                  type="button"
                  onClick={() => toggleUnitFilter(unit.id)}
                  className={clsx(
                    'w-full px-3 py-2 min-h-11 text-left text-sm text-gray-100 hover:bg-command-accent/20 rounded transition-colors flex items-center gap-2 touch-manipulation',
                    isSelected && 'bg-command-accent/30'
                  )}
                >
                  <div className={clsx(
                    'w-4 h-4 rounded border-2 flex items-center justify-center',
                    isSelected ? 'bg-command-accent border-command-accent' : 'border-gray-500'
                  )}>
                    {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                  </div>
                  {/* Unit color indicator */}
                  <div 
                    className="w-3 h-3 rounded-full border border-gray-600"
                    style={{ backgroundColor: unit.aorColor }}
                    title={`${unit.name} color`}
                  />
                  <div className="flex-1">
                    <div className="font-semibold">{unit.name}</div>
                    <div className="text-xs text-gray-400">{unit.code}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
